import React, { useEffect, useMemo, useState } from "react";

import { MenuItem } from "@mui/material";
import { Stack } from "@mui/material";
import ShipeeziNewGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import CheckItemJobNumberME from "../../../../../../../../../../../components/CheckItemJobNumberME";
import { Button, Select } from "../../../../../../../../../../../components/Form";
import { Color } from "../../../../../../../../../../../components/Form/Button";
import ActionButtons from "../../../../../../../../../../../components/Generic/ActionButtons";
import BadgeItemJobStatus from "../../../../../../../../../../../components/Generic/BadgeItemJobStatus";
import Drawer from "../../../../../../../../../../../components/Generic/Drawer";
import { useTransshipmentState } from "../../../../../../../../../../../context/TransshipmentContext";
import { ItemException } from "../../../../../../../../../../../models/item.exception";
import { ItemTypes } from "../../../../../../../../../../../models/item.types";
import { Order } from "../../../../../../../../../../../models/order";
import { Room } from "../../../../../../../../../../../models/room";
import { ItemJob } from "../../../../../../../../../../../models/sales";
import { Symbol } from "../../../../../../../../../../../models/symbol";
import { TransshipmentMovement } from "../../../../../../../../../../../models/transshipment";
import { calculateTotalVolume } from "../../../../../../../../../../../util/calculateTotalVolume";
import { Metric, handleGetConvertValue } from "../../../../../../../../../../../util/converts";
import { ItemJobSubStatus } from "../../../../../../../../../../../util/enums";
import { formatItemConditions } from "../../../../../../../../../../../util/formatItemConditions";
import { formatItemLocation } from "../../../../../../../../../../../util/formatItemLocation";
import { formatItemPacker } from "../../../../../../../../../../../util/formatItemPacker";
import { formatStorageBoxesFromOrder } from "../../../../../../../../../../../util/formatStorageBoxesFromOrder";
import { getMeasurement } from "../../../../../../../../../../../util/getMeasurement";
import { getOrderCustomerDisplayName } from "../../../../../../../../../../../util/names";
import { paginate } from "../../../../../../../../../../../util/paginate";
import {
  ButtonGroupStyled,
  Container,
  ContainerButtons,
  ContainerGrid,
  GridDetails,
  InfoDetails,
  InventoryContainer,
  Line,
  StackLineButton,
  TextMarkItems,
  Title,
  TitleDetails,
  useStyles,
} from "./styles";

enum Tab {
  LIST,
  BINGO,
}

type Props = {
  order?: Order;
  onSave: (order?: Order) => void;
  onCancel: () => void;
  isViewMode?: boolean;
};

const DrawerViewOrder = (props: Props) => {
  const { order, onSave, onCancel, isViewMode } = props;

  const { t: translate } = useTranslation();

  const { external, type, reference, storageBox, transshipment } = useTransshipmentState();

  const [tabSelected, setTabSelected] = useState<Tab>(Tab.LIST);

  const [orderUpdated, setOrderUpdated] = useState<Order>();

  const classes = useStyles();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const measurement = getMeasurement();

  const descriptionItemJob =
    order?.itemsJob && order?.itemsJob?.length > 1
      ? `${order?.itemsJob?.length || 0} ${translate("items")}`
      : `${order?.itemsJob?.length || 0} ${translate("item")}`;

  const itemsJobsPaginate = useMemo(() => {
    return paginate(order?.itemsJob || [], page > 0 ? page - 1 : 0, pageSize);
  }, [order?.itemsJob, page, pageSize]);

  useEffect(() => {
    setOrderUpdated(order);
  }, [order]);

  const renderTitleInfo = (title: string, info?: string) => {
    return (
      <Stack maxWidth="400px" direction="column">
        <TitleDetails>{title}</TitleDetails>
        <InfoDetails>{info || translate("empty")}</InfoDetails>
      </Stack>
    );
  };

  const isActiveTab = (tab: Tab) => {
    return tabSelected === tab;
  };

  const columns: Column[] = [
    {
      name: "storageBoxes",
      title: translate("location"),
      formatComponent: (value: string, row: ItemJob) => formatItemLocation(row),
      hideSort: true,
    },
    {
      name: "number",
      title: `${translate("item")}#`,
      hideSort: true,
      formatComponent: (value: string, row: ItemJob) => {
        if (row.meDone && row.meWorking && row.meWorkingDelivery && row.meDoneDelivery) {
          return <CheckItemJobNumberME value={value} />;
        }

        return <span>{value}</span>;
      },
    },
    {
      name: "description",
      title: translate("description"),
      hideSort: true,
    },
    {
      name: "itemType",
      title: translate("packingType"),
      formatText: (itemType: ItemTypes) => (itemType ? itemType?.name : ""),
      hideSort: true,
    },
    {
      name: "volume",
      title: translate("volume"),
      formatText: (volume: number) => {
        return `${handleGetConvertValue(
          volume || 0.0,
          measurement?.code,
          measurement?.decimalPlaces
        )} ${measurement?.code || Metric["m³"]}`;
      },
      hideSort: true,
    },
    {
      name: "packer",
      title: translate("packer"),
      formatText: (packer: string) => formatItemPacker(packer),
      hideSort: true,
    },
    {
      name: "room",
      title: translate("room"),
      formatText: (room: Room) => room?.name || "",
      hideSort: true,
    },
    {
      name: "symbol",
      title: translate("descriptiveSymbol"),
      formatText: (symbol: Symbol) => (symbol ? symbol?.name : ""),
      hideSort: true,
    },
    {
      name: "exceptions",
      title: translate("condition"),
      formatText: (exceptions: ItemException[]) => formatItemConditions(exceptions, false),
      hideSort: true,
    },

    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => <BadgeItemJobStatus value={status} />,
      hideSort: true,
    },
  ];

  const updateMovementsBySelectedItems = (itemsSelected: string[]) => {
    if (itemsSelected && itemsSelected?.length) {
      const movements = orderUpdated?.movements || [];
      const movementsUpdated: TransshipmentMovement[] = [];

      itemsSelected.forEach((itemSelected) => {
        const movement = movements.find(
          (movement) => movement?.itemJobId?.toString() === itemSelected
        );

        if (movement) {
          movementsUpdated.push(movement);
        } else {
          movementsUpdated.push({
            itemJobId: Number(itemSelected),
            isExternal: external,
            storageBoxId: storageBox?.id,
            jobId: orderUpdated?.id,
            referenceSearch: reference,
            typeSearch: type,
            whatToDo: transshipment?.whatToDo,
          } as TransshipmentMovement);
        }
      });

      setOrderUpdated((prev) => {
        return { ...prev, movements: movementsUpdated };
      });
    } else {
      setOrderUpdated((prev) => {
        return { ...prev, movements: [] };
      });
    }
  };

  const renderMarkItemsText = () => {
    if (orderUpdated?.movements?.length)
      return `${translate("transshiping")} ${orderUpdated?.movements?.length || 0} ${
        orderUpdated?.movements?.length > 1 ? translate("items") : translate("item")
      }`;

    return translate("markItemsYouWantToMove");
  };

  return (
    <Drawer
      open={!!order}
      onClose={() => onCancel()}
      title={`${translate("order")}  ${order?.reference} - ${getOrderCustomerDisplayName(order)}`}
    >
      <Container>
        <Title>{translate("details")}</Title>
      </Container>
      <GridDetails>
        {renderTitleInfo(translate("orderType"), order?.type)}
        {renderTitleInfo(translate("reference"), order?.reference)}
        {renderTitleInfo(translate("branchOrigin"), order?.branchFrom?.name)}
        {renderTitleInfo(translate("branchDestination"), order?.toBranch?.name)}
        {renderTitleInfo(translate("status"), order?.status)}
        {renderTitleInfo(
          translate("inventoryActual"),
          `${descriptionItemJob} - ${calculateTotalVolume(
            ItemJobSubStatus.ACTUAL,
            measurement,
            order?.itemsJob
          )}`
        )}
        {renderTitleInfo(translate("mode"), order?.mode ? translate(order.mode.toLowerCase()) : "")}
        {renderTitleInfo(
          translate("storageBoxes"),
          formatStorageBoxesFromOrder(order?.itemsJob || [])
        )}
      </GridDetails>
      <InventoryContainer>
        <Title>{translate("inventory")}</Title>
      </InventoryContainer>
      <ContainerGrid>
        <Stack direction="column" spacing="16px">
          <TextMarkItems>{!isViewMode && renderMarkItemsText()}</TextMarkItems>
          <Stack direction="row" spacing={1} alignItems="flex-end" maxWidth="600px">
            <Select name="show" value="perItem" label={translate("show")}>
              <MenuItem value="perItem">{translate("perItem")}</MenuItem>
            </Select>
            <ButtonGroupStyled fullWidth>
              <Button
                data-cy="roomButton"
                onClick={() => setTabSelected(Tab.LIST)}
                customColor={isActiveTab(Tab.LIST) ? Color.SECONDARY : Color.NOT_SELECTED}
                className={classes.buttonGroup}
              >
                {translate("list")}
              </Button>
              <Button
                disabled
                data-cy="itemButtonToList"
                onClick={() => setTabSelected(Tab.BINGO)}
                customColor={isActiveTab(Tab.BINGO) ? Color.SECONDARY : Color.NOT_SELECTED}
                className={classes.buttonGroup}
              >
                {translate("bingoSheet")}
              </Button>
            </ButtonGroupStyled>
          </Stack>

          <ShipeeziNewGrid
            data={itemsJobsPaginate || []}
            totalItems={order?.itemsJob?.length}
            columns={columns}
            positionActionsColumn="right"
            clearSelection={clearSelection}
            keyExtractor={(itemJob) => itemJob?.id?.toString() || itemJob?.uuid}
            selecteds={
              orderUpdated?.movements && orderUpdated?.movements?.length
                ? orderUpdated?.movements?.map((movement) => movement?.itemJobId?.toString())
                : undefined
            }
            handleSelect={(selectedItems: string[]) => {
              updateMovementsBySelectedItems(selectedItems);
              setClearSelection(false);
            }}
            showSelection={!isViewMode}
            emptyData={translate("noInformationToBeShown")}
            currentPage={page}
            pageSizeList={[10, 20, 40, 60]}
            pageSize={pageSize}
            onChangePageSize={setPageSize}
            onNextPage={setPage}
            onPreviousPage={setPage}
            onGoToPage={setPage}
          />
        </Stack>
      </ContainerGrid>

      <StackLineButton direction="column">
        <Line />
        <ContainerButtons>
          <ActionButtons
            onSave={isViewMode ? undefined : () => onSave(orderUpdated)}
            onCancel={() => onCancel()}
          />
        </ContainerButtons>
      </StackLineButton>
    </Drawer>
  );
};

export default DrawerViewOrder;
